import { UserPreferencesUnitOfAreaEnum } from "components/modal/settings/unitOfArea";
import { getCompactUnitData, getFormatterAndRegion, tooltipCurrencyOptions, tooltipUnitOptions } from "./number-formatter-helpers";
import { NumberFormatKind } from "./types";
const defaultOptions = {
    maximumFractionDigits: 2,
    signDisplay: "auto"
};
const formatPercent = (region, currentOptions, value, formatter) => {
    const result = `${formatter(value, region, {
        ...{
            maximumFractionDigits: currentOptions.maximumFractionDigits,
            maximumSignificantDigits: currentOptions.maximumSignificantDigits,
            minimumFractionDigits: currentOptions.minimumFractionDigits,
            minimumSignificantDigits: currentOptions.minimumSignificantDigits,
            signDisplay: currentOptions.signDisplay,
            style: "percent"
        }
    })}`.trimEnd();
    return result === "(0%)" || result === "-0%" ? "0%" : result;
};
const formatCompact = (currentOptions, formatUnit, region, value, formatter) => {
    const formatOptions = {
        maximumFractionDigits: currentOptions.maximumFractionDigits,
        maximumSignificantDigits: currentOptions.maximumSignificantDigits,
        minimumFractionDigits: currentOptions.minimumFractionDigits,
        minimumSignificantDigits: currentOptions.minimumSignificantDigits,
        signDisplay: currentOptions.signDisplay
    };
    if (!formatUnit) {
        return `${formatter(value, region, { ...{
                ...formatOptions,
                compactDisplay: "short",
                notation: "compact"
            } })}`.trimEnd();
    }
    const [adaptedValue, unit] = getCompactUnitData(region ?? "", value, formatUnit);
    const result = `${formatter(adaptedValue, region, { ...{
            ...formatOptions,
            notation: "standard"
        } })}`.trimEnd();
    const resultWithUnit = `${result}${unit}`;
    return resultWithUnit;
};
const formatCurrency = (currentOptions, formatter, value, region) => {
    if ((currentOptions.maximumFractionDigits ?? 2) < 2 && !currentOptions.minimumFractionDigits) {
        // https://github.com/andyearnshaw/Intl.js/issues/123 for more info on this
        currentOptions.minimumFractionDigits = 0;
    }
    return `${formatter(value, region, { ...tooltipCurrencyOptions, ...currentOptions })} ${currentOptions.currency ?? ""}`
        .trimEnd();
};
const formatAreaUnit = (currentOptions, formatter, value, region) => {
    if ((currentOptions.maximumFractionDigits ?? 2) < 2 && !currentOptions.minimumFractionDigits) {
        // https://github.com/andyearnshaw/Intl.js/issues/123 for more info on this
        currentOptions.minimumFractionDigits = 0;
    }
    if (currentOptions.unitOfArea?.toUpperCase() === UserPreferencesUnitOfAreaEnum.SquareMeter.toUpperCase()) {
        currentOptions.unitOfArea = "Sq. Meter";
    }
    if (currentOptions.unitOfArea?.toUpperCase() === UserPreferencesUnitOfAreaEnum.SquareFoot.toUpperCase()) {
        currentOptions.unitOfArea = "Sq. Feet";
    }
    return `${formatter(value, region, { ...tooltipUnitOptions, ...currentOptions, style: "decimal" })} ${currentOptions.unitOfArea}`
        .trimEnd();
};
const formatValueUnit = (currentOptions, formatter, value, region) => {
    if ((currentOptions.maximumFractionDigits ?? 2) < 2 && !currentOptions.minimumFractionDigits) {
        // https://github.com/andyearnshaw/Intl.js/issues/123 for more info on this
        currentOptions.minimumFractionDigits = 0;
    }
    return `${formatter(value, region, { ...tooltipCurrencyOptions, ...currentOptions, style: "decimal" })} ${currentOptions.unitForValue ?? ""}`
        .trimEnd();
};
const formatDefault = (region, currentOptions, value, formatter) => `${formatter(value, region, { ...currentOptions,
    ...{
        maximumFractionDigits: currentOptions.maximumFractionDigits,
        maximumSignificantDigits: currentOptions.maximumSignificantDigits,
        minimumFractionDigits: currentOptions.minimumFractionDigits,
        minimumSignificantDigits: currentOptions.minimumSignificantDigits,
        signDisplay: currentOptions.signDisplay
    } })}`.trimEnd();
const kindFormatter = (value, kind = NumberFormatKind.default, options = defaultOptions, formatUnit = "") => {
    const currentOptions = { ...defaultOptions, ...options };
    const { formatter, region } = getFormatterAndRegion();
    switch (kind) {
        case NumberFormatKind.percent: {
            return formatPercent(region, currentOptions, value, formatter);
        }
        case NumberFormatKind.compact: {
            return formatCompact(currentOptions, formatUnit, region, value, formatter);
        }
        case NumberFormatKind.currency: {
            return formatCurrency(currentOptions, formatter, value, region);
        }
        case NumberFormatKind.area: {
            return formatAreaUnit(currentOptions, formatter, value, region);
        }
        case NumberFormatKind.multiple: {
            return `${formatter(value, region, currentOptions)}x`;
        }
        case NumberFormatKind.unit: {
            return formatValueUnit(currentOptions, formatter, value, region);
        }
        default:
            return formatDefault(region, currentOptions, value, formatter);
    }
};
export const numberFormatters = (value, kind = NumberFormatKind.default, options = defaultOptions, formatUnit = "") => {
    if (value === null || isNaN(value)) {
        return "-";
    }
    let numberFormatterOptions = {};
    /*
     * As per Intl.NumberFormat library which is used for number formatting on UI:
     * The following properties fall into two groups: minimumIntegerDigits, minimumFractionDigits,
     * and maximumFractionDigits in one group, minimumSignificantDigits and maximumSignificantDigits
     * in the other. If at least one property from the second group is defined, then the first group
     * is ignored. In order to have only two decimals for numbers between 1 and -1 we are doing
     * custom mapping without minimumSignificantDigits and maximumSignificantDigits.
     */
    if (value < 1 && value > -1 && value !== 0) {
        numberFormatterOptions = {
            compactDisplay: options.compactDisplay,
            currency: options.currency,
            maximumFractionDigits: options.maximumFractionDigits ?? 2,
            minimumFractionDigits: kind === NumberFormatKind.currency ? options.minimumFractionDigits ?? 2 : options.minimumFractionDigits ?? 0,
            notation: options.notation,
            signDisplay: options.signDisplay,
            unitForValue: options.unitForValue,
            unitOfArea: options.unitOfArea
        };
    }
    else {
        numberFormatterOptions = { ...options };
    }
    return kindFormatter(value, kind, numberFormatterOptions, formatUnit);
};
export const parseFromString = (value, decimalSeparator, thousandsSeparator) => parseFloat(value.replaceAll(thousandsSeparator, "").replace(decimalSeparator, "."));
